import { useState, useRef, useCallback } from "react";
import { Reveal } from "./Reveal";
import { ImageIcon } from "lucide-react";

// Per inserire le foto reali: caricare i file in /frontend/public/casi/
// e impostare qui i percorsi, es. before: "/casi/caso-1-prima.jpg"
const cases = [
  {
    label: "Carico immediato in giornata",
    detail: "Intervento di implantologia a carico immediato con provvisorio fisso inserito in giornata",
    before: "/casi/caso-1-prima.jpg",
    after: "/casi/caso-1-dopo.jpg",
  },
  {
    label: "Mimma — All-on-4 arcata superiore",
    detail: "Impianti a carico immediato in giornata, All-on-4 sull'arcata superiore",
    before: "/casi/caso-2-prima.jpg",
    after: "/casi/caso-2-dopo.jpg",
  },
  {
    label: "Carico immediato superiore e inferiore",
    detail: "Entrambe le arcate in giornata, con inserimento del provvisorio fisso",
    before: "/casi/caso-3-prima.jpg",
    after: "/casi/caso-3-dopo.jpg",
  },
  {
    label: "Antonia — Doppio carico (due arcate)",
    detail: "Doppio carico immediato in giornata su arcata superiore e inferiore",
    before: "/casi/caso-4-prima.jpg",
    after: "/casi/caso-4-dopo.jpg",
  },
];

const Panel = ({ src, kind, label }) => {
  if (src) {
    return <img src={src} alt={`${kind === "before" ? "Prima" : "Dopo"} — ${label}`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable="false" />;
  }
  const dark = kind === "before";
  return (
    <div className={`absolute inset-0 flex flex-col items-center justify-center gap-3 ${dark ? "bg-[#2A3530]" : "bg-[#DDEBF2]"}`}>
      <ImageIcon size={40} strokeWidth={1.4} className={dark ? "text-white/30" : "text-[#385244]/30"} />
      <p className={`text-lg font-serif-display ${dark ? "text-white/70" : "text-[#385244]"}`}>{dark ? "Prima" : "Dopo"}</p>
      <p className={`text-xs font-semibold uppercase tracking-wider ${dark ? "text-white/40" : "text-[#5C6B61]"}`}>Foto reale in arrivo</p>
    </div>
  );
};

export const Slider = ({ item, i }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const move = useCallback((clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  return (
    <div data-testid={`before-after-case-${i}`}>
      <div
        ref={ref}
        className="relative aspect-[4/3] rounded-[28px] overflow-hidden select-none cursor-ew-resize border border-border touch-none"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); move(e.clientX); }}
        onPointerMove={(e) => e.buttons === 1 && move(e.clientX)}
        data-testid={`before-after-slider-${i}`}
      >
        <Panel src={item.after} kind="after" label={item.label} />
        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <Panel src={item.before} kind="before" label={item.label} />
        </div>
        <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#385244] font-bold text-sm shadow-lg">
            ⇄
          </span>
        </div>
        <span className="absolute top-4 left-4 bg-[#15201A]/70 backdrop-blur text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">Prima</span>
        <span className="absolute top-4 right-4 bg-[#385244]/90 backdrop-blur text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">Dopo</span>
      </div>
      <p className="mt-4 font-serif-display text-xl text-[#15201A]">{item.label}</p>
      <p className="text-sm text-[#5C6B61] mt-1">{item.detail}</p>
    </div>
  );
};

export const BeforeAfter = () => (
  <section id="prima-dopo" className="py-24 md:py-32 px-6 md:px-12" data-testid="before-after-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#7D9285] mb-4">Casi clinici — All-on-4</p>
        <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#15201A] max-w-3xl">
          Prima e <em className="italic text-[#385244]">dopo</em>: casi reali di denti fissi All on 4
        </h2>
        <p className="mt-6 text-base md:text-lg leading-relaxed text-[#5C6B61] max-w-2xl">
          Trascina il cursore per confrontare i risultati.
        </p>
        <p className="mt-4 text-base md:text-lg leading-relaxed text-[#15201A] font-semibold max-w-2xl" data-testid="before-after-real-note">
          Le immagini sono reali e la foto del dopo intervento si riferisce al provvisorio
          <strong> fisso</strong> realizzato in giornata 😊
        </p>
        <p className="mt-3 text-sm leading-relaxed text-[#5C6B61] max-w-2xl">
          A distanza di circa 5 mesi vengono realizzati i lavori definitivi fissi.
          Ogni caso clinico è unico e viene valutato dal direttore sanitario.
        </p>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 gap-8">
        {cases.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.1}>
            <Slider item={c} i={i} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
